// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.

import React, { useMemo } from 'react';
import { Card, IconButton, Link, Tag, Typography } from '@kintent/glide';
import { UilBell } from '@iconscout/react-unicons';

import * as Styles from './NotificationSection.styles';
import { Flex } from '../../../../../components/Flex';
import CustomTag from '../../../../../components/CustomTag/CustomTag';
import CustomButton from '../../../../../components/CustomButton/CustomButton';
import { useProgramContent, useTrustShareNotifications } from '../../../../../lib/state';
import { getPublicNotifications } from '../../../../../lib/utils';
import {
  NOTIFICATION_EMPTY_STATE_COPY,
  NOTIFICATION_TYPE_COLOR_VARIANTS,
  TRUSTSHARE_NOTIFICATION_TYPE_LABELS,
} from '../../../../../lib/constants';

// Constants
const MAX_NOTIFICATIONS = 3;

const NOTIFICATION_LIST_PAGE_LINK = '/notifications';

function NotificationListItem({ notification }) {
  return (
    <Card className="notification-list-item">
      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Typography
          as="p"
          level="5"
          className="notification-item-title"
        >
          {notification.title}
        </Typography>
        <Tag
          type="category"
          variant={NOTIFICATION_TYPE_COLOR_VARIANTS[notification.type]}
        >
          {TRUSTSHARE_NOTIFICATION_TYPE_LABELS[notification.type]}
        </Tag>
      </Flex>
      {notification.description && (
        <Typography
          as="p"
          level="8"
          strength="60%"
          className="notification-item-description"
          dangerouslySetInnerHTML={{ __html: notification.description }}
        />
      )}
      <Flex
        className="notification-item-actions"
        gap="12px"
      >
        <IconButton
          label="Subscribe"
          className="notification-subscribe-icon-button"
          icon={<UilBell size="1em" />}
          data-testid="notification-subscribe-button"
        />
        <CustomButton
          as={Link}
          className="secondary"
          href={NOTIFICATION_LIST_PAGE_LINK}
        >
          Learn More
        </CustomButton>
      </Flex>
    </Card>
  );
}

function NotificationSection() {
  const [{ secondarySectionTitle }] = useProgramContent();
  const { data: allNotifications } = useTrustShareNotifications();
  const publicNotifications = useMemo(() => getPublicNotifications(allNotifications), [allNotifications]);

  // Display notifications based on what a user has selected to be displayed and limit it to 3
  const notificationsToDisplay = publicNotifications.filter((i) => i.shouldDisplay).slice(0, MAX_NOTIFICATIONS);

  return (
    <Styles.Section data-testid="notification-section">
      <Styles.GridItemHeading>
        <Flex
          alignItems="center"
          gap="12px"
        >
          <CustomTag>{publicNotifications.length}</CustomTag>
          <Typography
            as="h5"
            level="5"
            className="notification-section-title"
          >
            {secondarySectionTitle}
          </Typography>
        </Flex>
        <Typography
          as={Link}
          href={NOTIFICATION_LIST_PAGE_LINK}
        >
          Show All
        </Typography>
      </Styles.GridItemHeading>
      <Styles.NotificationListGrid>
        {publicNotifications.length > 0 ? (
          <>
            {notificationsToDisplay.map((notification) => {
              return (
                <NotificationListItem
                  key={notification.id}
                  notification={notification}
                />
              );
            })}
          </>
        ) : (
          <Typography
            as="p"
            strength="60%"
            level="8"
            className="notification-section-empty-state-text"
          >
            {NOTIFICATION_EMPTY_STATE_COPY}
          </Typography>
        )}
      </Styles.NotificationListGrid>
      <div className="show-all-notifications-cta">
        <Typography
          as={Link}
          href={NOTIFICATION_LIST_PAGE_LINK}
        >
          Show All Notifications
        </Typography>
      </div>
    </Styles.Section>
  );
}

export default NotificationSection;
